import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
 Clock,
 BarChart3,
 Users,
 AlertTriangle,
 CheckCircle,
 XCircle,
 Activity,
 User,
 Download,
 Filter,
 TrendingUp,
 Calendar,
 Timer,
 Award,
} from 'lucide-react';
import { ComingSoonBanner } from '../ComingSoonBanner';

const mockTeamActivity = [
 { name: 'Sarah Johnson', action: 'Clocked in', time: '9:05 AM', status: 'late' },
 { name: 'Mike Chen', action: 'Break started', time: '10:30 AM', status: 'normal' },
 { name: 'Lisa Wang', action: 'Clocked out', time: '5:00 PM', status: 'early' },
 { name: 'James Brown', action: 'Overtime started', time: '6:00 PM', status: 'overtime' },
];

const getActivityStatusColor = (status: string) => {
 switch (status) {
 case 'late':
 return 'bg-red-500';
 case 'early':
 return 'bg-orange-500';
 case 'overtime':
 return 'bg-blue-500';
 default:
 return 'bg-green-500';
 }
};

export default function AttendanceDashboard() {
 const { t } = useTranslation();
 const [userRole, setUserRole] = useState<'employee' | 'manager' | 'hr'>('employee');

 return (
 <div className="p-6 max-w-7xl mx-auto">
 <ComingSoonBanner
 icon={<Clock className="w-6 h-6" />}
 title="Attendance Dashboard"
 description="Attendance Dashboard is currently in development. This is a preview of the upcoming feature."
 gradientFrom="from-teal-500"
 gradientTo="to-emerald-500"
 />

 {/* Header Section */}
 <div className="bg-gradient-to-r from-blue-50 via-indigo-50 to-blue-50 rounded-2xl border border-border p-8 mb-6">
 <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
 <div className="flex items-center mb-4 lg:mb-0">
 <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg mr-4">
 <BarChart3 className="w-6 h-6 text-white" />
 </div>
 <div>
 <h1 className="text-3xl font-bold text-gray-900">{t('Attendance Dashboard')}</h1>
 <p className="text-gray-600">
 {userRole === 'employee' && t('Your personalized attendance overview and performance tracker')}
 {userRole === 'manager' && t('Team attendance insights and management tools')}
 {userRole === 'hr' && t('Company-wide attendance analytics and policy monitoring')}
 </p>
 </div>
 </div>

 {/* Role Switcher */}
 <div className="flex items-center space-x-2">
 <span className="text-sm text-gray-500">{t('View as')}:</span>
 <div className="flex bg-card rounded-lg p-1 shadow-sm border border-border">
 {(['employee', 'manager', 'hr'] as const).map((role) => (
 <button
 key={role}
 onClick={() => setUserRole(role)}
 className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${
 userRole === role
 ? 'bg-teal-500 text-white shadow-sm'
 : 'text-gray-500 hover:text-gray-700'
 }`}
 >
 {role.charAt(0).toUpperCase() + role.slice(1)}
 </button>
 ))}
 </div>
 </div>
 </div>
 </div>

 {/* Action Bar */}
 <div className="flex items-center justify-end gap-3 mb-6">
 <button
 className="flex items-center gap-2 px-4 py-2 border border-border text-gray-700 rounded-lg"
 >
 <Filter className="w-4 h-4" />
 {t('Filter')}
 </button>
 <button
 className="flex items-center gap-2 px-4 py-2 border border-border text-gray-700 rounded-lg"
 >
 <Download className="w-4 h-4" />
 {t('Export')}
 </button>
 </div>

 {/* Employee View - Today's Status + Gamification */}
 <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
 {/* Today's Status */}
 <div className="bg-card rounded-xl shadow-sm border border-border p-6">
 <div className="flex items-center justify-between mb-4">
 <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
 <Clock className="w-5 h-5 text-teal-600" />
 {t("Today's Status")}
 </h3>
 <div className="flex items-center gap-2">
 <div className="w-2.5 h-2.5 bg-green-500 rounded-full animate-pulse"></div>
 <span className="text-xs text-green-600 font-medium">{t('Online')}</span>
 </div>
 </div>
 <div className="space-y-4">
 <div className="flex items-center justify-between p-4 bg-teal-50 rounded-lg">
 <div>
 <p className="text-sm text-gray-600">{t('Clock In')}</p>
 <p className="text-xl font-bold text-gray-900">08:55 AM</p>
 </div>
 <CheckCircle className="w-8 h-8 text-green-500" />
 </div>
 <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
 <div>
 <p className="text-sm text-gray-600">{t('Expected Clock Out')}</p>
 <p className="text-xl font-bold text-gray-900">05:55 PM</p>
 </div>
 <Timer className="w-8 h-8 text-gray-700" />
 </div>
 <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
 <div>
 <p className="text-sm text-gray-600">{t('Hours Worked')}</p>
 <p className="text-xl font-bold text-gray-900">6h 32m</p>
 </div>
 <TrendingUp className="w-8 h-8 text-blue-500" />
 </div>
 </div>
 </div>

 {/* Gamification */}
 <div className="bg-card rounded-xl shadow-sm border border-border p-6">
 <div className="flex items-center justify-between mb-4">
 <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
 <Award className="w-5 h-5 text-amber-500" />
 {t('Attendance Score')}
 </h3>
 </div>
 <div className="flex flex-col items-center py-4">
 <div className="relative w-32 h-32 mb-4">
 <svg className="w-32 h-32 transform -rotate-90" viewBox="0 0 36 36">
 <path
 className="text-gray-200"
 d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
 fill="none"
 stroke="currentColor"
 strokeWidth="3"
 />
 <path
 className="text-teal-500"
 d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
 fill="none"
 stroke="currentColor"
 strokeWidth="3"
 strokeDasharray="92, 100"
 />
 </svg>
 <div className="absolute inset-0 flex items-center justify-center">
 <span className="text-3xl font-bold text-gray-900">92%</span>
 </div>
 </div>
 <p className="text-sm text-gray-600 mb-2">{t('Excellent attendance this month!')}</p>
 <div className="flex gap-4 text-sm">
 <div className="text-center">
 <p className="font-bold text-green-600">22</p>
 <p className="text-gray-500">{t('On Time')}</p>
 </div>
 <div className="text-center">
 <p className="font-bold text-amber-600">1</p>
 <p className="text-gray-500">{t('Late')}</p>
 </div>
 <div className="text-center">
 <p className="font-bold text-red-600">0</p>
 <p className="text-gray-500">{t('Absent')}</p>
 </div>
 </div>
 </div>
 </div>
 </div>

 {/* Monthly Summary + Recent Logs */}
 <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
 {/* Monthly Summary */}
 <div className="bg-card rounded-xl shadow-sm border border-border p-6">
 <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
 <Calendar className="w-5 h-5 text-indigo-600" />
 {t('Monthly Summary')}
 </h3>
 <div className="grid grid-cols-2 gap-4">
 <div className="p-4 bg-green-50 rounded-lg">
 <div className="flex items-center gap-2 mb-1">
 <CheckCircle className="w-4 h-4 text-green-600" />
 <span className="text-sm font-medium text-gray-700">{t('Present')}</span>
 </div>
 <p className="text-2xl font-bold text-green-700">22</p>
 </div>
 <div className="p-4 bg-red-50 rounded-lg">
 <div className="flex items-center gap-2 mb-1">
 <XCircle className="w-4 h-4 text-red-600" />
 <span className="text-sm font-medium text-gray-700">{t('Absent')}</span>
 </div>
 <p className="text-2xl font-bold text-red-700">0</p>
 </div>
 <div className="p-4 bg-amber-50 rounded-lg">
 <div className="flex items-center gap-2 mb-1">
 <AlertTriangle className="w-4 h-4 text-amber-600" />
 <span className="text-sm font-medium text-gray-700">{t('Late')}</span>
 </div>
 <p className="text-2xl font-bold text-amber-700">1</p>
 </div>
 <div className="p-4 bg-blue-50 rounded-lg">
 <div className="flex items-center gap-2 mb-1">
 <Clock className="w-4 h-4 text-blue-600" />
 <span className="text-sm font-medium text-gray-700">{t('Overtime')}</span>
 </div>
 <p className="text-2xl font-bold text-blue-700">3.5h</p>
 </div>
 </div>
 </div>

 {/* Recent Team Activity */}
 <div className="bg-card rounded-xl shadow-sm border border-border">
 <div className="p-6 border-b border-border">
 <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
 <Activity className="w-5 h-5 text-purple-600" />
 {t('Recent Team Activity')}
 </h3>
 </div>
 <div className="p-6">
 <div className="space-y-4">
 {mockTeamActivity.map((activity, index) => (
 <div
 key={index}
 className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
 >
 <div className="flex items-center space-x-3">
 <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center">
 <User className="w-4 h-4 text-teal-700" />
 </div>
 <div>
 <div className="text-sm font-medium text-gray-900">{activity.name}</div>
 <div className="text-xs text-gray-500">{activity.action}</div>
 </div>
 </div>
 <div className="flex items-center space-x-2">
 <span className="text-xs text-gray-500">{activity.time}</span>
 <div className={`w-2 h-2 rounded-full ${getActivityStatusColor(activity.status)}`}></div>
 </div>
 </div>
 ))}
 </div>
 </div>
 </div>
 </div>

 {/* Company-wide Stats (HR view preview) */}
 <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
 <div className="bg-card rounded-lg shadow-sm border border-border p-6">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm font-medium text-gray-600">{t('Total Employees')}</p>
 <p className="text-2xl font-bold text-gray-900">1,267</p>
 </div>
 <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
 <Users className="w-6 h-6 text-blue-600" />
 </div>
 </div>
 <p className="text-sm text-green-600 mt-2">98.2% {t('present today')}</p>
 </div>

 <div className="bg-card rounded-lg shadow-sm border border-border p-6">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm font-medium text-gray-600">{t('On Time Today')}</p>
 <p className="text-2xl font-bold text-gray-900">1,198</p>
 </div>
 <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
 <CheckCircle className="w-6 h-6 text-green-600" />
 </div>
 </div>
 <p className="text-sm text-green-600 mt-2">94.6% {t('punctuality')}</p>
 </div>

 <div className="bg-card rounded-lg shadow-sm border border-border p-6">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm font-medium text-gray-600">{t('Late Arrivals')}</p>
 <p className="text-2xl font-bold text-gray-900">46</p>
 </div>
 <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
 <AlertTriangle className="w-6 h-6 text-amber-600" />
 </div>
 </div>
 <p className="text-sm text-amber-600 mt-2">3.6% {t('of workforce')}</p>
 </div>

 <div className="bg-card rounded-lg shadow-sm border border-border p-6">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm font-medium text-gray-600">{t('On Leave')}</p>
 <p className="text-2xl font-bold text-gray-900">23</p>
 </div>
 <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
 <Calendar className="w-6 h-6 text-purple-600" />
 </div>
 </div>
 <p className="text-sm text-purple-600 mt-2">1.8% {t('of workforce')}</p>
 </div>
 </div>
 </div>
 );
}
